import { AgentStaffActionStatus, type RawInteractionRow } from "../../utils/models";

/** Minimal shape of i18next's `t` — these helpers are pure (non-hook), so the caller passes `t` in. */
export type TranslateFn = (key: string) => string;

/**
 * Stable, language-independent classification of a shift's final status. Both the status icon and the
 * localized label derive from this, so translating the label can never desync the icon (which used to
 * substring-match the English text).
 */
export enum ShiftFinalStatusKind {
	DoneAccomplished = "done_accomplished",
	DoneNotAccomplished = "done_not_accomplished",
	DonePartially = "done_partially",
	Pending = "pending",
	Paused = "paused",
	NotInitiated = "not_initiated",
	Skipped = "skipped",
	Canceled = "canceled",
	Other = "other",
}

// Non-completed status → kind. "canceled" has no AgentStaffActionStatus member, so it's matched as a raw string.
const RAW_STATUS_KIND: Record<string, ShiftFinalStatusKind> = {
	[AgentStaffActionStatus.Pending]: ShiftFinalStatusKind.Pending,
	[AgentStaffActionStatus.Paused]: ShiftFinalStatusKind.Paused,
	[AgentStaffActionStatus.NotInitiated]: ShiftFinalStatusKind.NotInitiated,
	[AgentStaffActionStatus.Skipped]: ShiftFinalStatusKind.Skipped,
	canceled: ShiftFinalStatusKind.Canceled,
};

const STATUS_KIND_LABEL_KEY: Record<Exclude<ShiftFinalStatusKind, ShiftFinalStatusKind.Other>, string> = {
	[ShiftFinalStatusKind.DoneAccomplished]: "shift_pickup_report.status.done_accomplished",
	[ShiftFinalStatusKind.DoneNotAccomplished]: "shift_pickup_report.status.done_not_accomplished",
	[ShiftFinalStatusKind.DonePartially]: "shift_pickup_report.status.done_partially",
	[ShiftFinalStatusKind.Pending]: "shift_pickup_report.status.pending",
	[ShiftFinalStatusKind.Paused]: "shift_pickup_report.status.paused",
	[ShiftFinalStatusKind.NotInitiated]: "shift_pickup_report.status.not_initiated",
	[ShiftFinalStatusKind.Skipped]: "shift_pickup_report.status.skipped",
	[ShiftFinalStatusKind.Canceled]: "shift_pickup_report.status.canceled",
};

/**
 * Classify a shift's final status.
 *
 * - A completed agent run is refined by how many nurses accepted vs. were needed:
 *   accomplished (enough), not-accomplished (none), partial (some).
 * - Any other status maps to its own kind (or `Other` for an unrecognized value).
 */
export const getShiftFinalStatusKind = (status: AgentStaffActionStatus | string, approvedCount: number, neededNurses: number): ShiftFinalStatusKind => {
	if (status === AgentStaffActionStatus.Completed) {
		if (approvedCount >= neededNurses) {
			return ShiftFinalStatusKind.DoneAccomplished;
		}
		if (approvedCount === 0) {
			return ShiftFinalStatusKind.DoneNotAccomplished;
		}
		return ShiftFinalStatusKind.DonePartially;
	}
	return RAW_STATUS_KIND[status] ?? ShiftFinalStatusKind.Other;
};

/**
 * The shift's final-status label as shown in both the grouped and raw views, localized via `t`.
 * For an unrecognized status (`Other`), the raw value is title-cased and underscores spaced out
 * (e.g. `not_initiated` → "Not initiated") as a safe fallback.
 *
 * Shared by both views so the label is derived identically.
 */
export const getShiftFinalStatusLabel = (t: TranslateFn, status: AgentStaffActionStatus | string, approvedCount: number, neededNurses: number): string => {
	const kind = getShiftFinalStatusKind(status, approvedCount, neededNurses);
	if (kind === ShiftFinalStatusKind.Other) {
		return status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, " ");
	}
	return t(STATUS_KIND_LABEL_KEY[kind]);
};

/**
 * Confirmation-status cell value for one raw interaction row, applying the decided placement rule:
 *
 * - If the shift had any acceptance (`approved_count >= 1` — accomplished OR partial) → the shift's
 *   final-status label appears ONLY on the row(s) of the nurse(s) who actually accepted
 *   (`is_approved`); every other contact row for that shift is blank.
 * - Otherwise (no acceptances) → the shift's final-status label appears on every row of the shift.
 */
export const getConfirmationStatusCell = (t: TranslateFn, row: RawInteractionRow): string => {
	const label = getShiftFinalStatusLabel(t, row.request_status, row.approved_count, row.needed_nurses);

	if (row.approved_count >= 1) {
		return row.is_approved ? label : "";
	}

	return label;
};
